/**
 * Loads the Amplitude tracking script and synchronizes Ids to the server to use with the server-side Amplitude API.
 * Provides static track method for sending events to Amplitude.
 */

import { MiaPlaza } from "@/Reinforced.Typings";
import axios from "axios";
import AmplitudeLinkTagger from "./AmplitudeLinkTagger";

interface AmplitudeEvent {
	eventType: string
	eventProperties?: Record<string, any>
}

let amplitudeInstance: Amplitude | null = null;

/**
 * This is used to ensure that events that happen before load() is called are still sent.
 * This queue does not persist page reloads.
 */
const amplitudeEventQueue: AmplitudeEvent[] = [];

export default class Amplitude {
	private readonly options: MiaPlaza.Control.Tracking.IAmplitudeTrackingOptions;
	private loaded = false;
	private amplitude: typeof import("@amplitude/analytics-browser") | null = null;

	constructor(options: MiaPlaza.Control.Tracking.IAmplitudeTrackingOptions) {
		this.options = options;
	}

	public async load(enableAttributionAutocapture: boolean): Promise<void> {
		if (this.loaded) {
			return;
		}
		this.loaded = true;

		this.amplitude = await import("@amplitude/analytics-browser");

		await this.amplitude.init(this.options.ApiKey, this.options.UserId ?? undefined, {
			defaultTracking: true,
			autocapture: {
				attribution: !enableAttributionAutocapture ? false : {
					// avoid self-referrals from our other sites.
					excludeReferrers: this.options.DomainsServedByOtherSites,
				},
				// see https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#track-element-interactions
				elementInteractions: true,
			},
		}).promise;

		amplitudeInstance = this;
		this.flushEventQueue();

		if (this.options.SessionReplayEnabled) {
			try {
				const { sessionReplayPlugin } = await import("@amplitude/plugin-session-replay-browser");
				this.amplitude.add(sessionReplayPlugin());
			}
			catch {
				// Best-effort: swallow the error so a failed replay plugin load does not affect analytics.
			}
		}

		const sessionId = this.amplitude.getSessionId();
		const deviceId = this.amplitude.getDeviceId();

		// This shouldn't happen, but can if a user has very strict browser settings
		if (!sessionId || !deviceId) {
			return;
		}

		await this.synchronizeTrackingIdsWithServer(sessionId, deviceId);
		if (this.options.CrossDomainLinkTaggingEnabled) {
			new AmplitudeLinkTagger(this.options.DomainsServedByOtherSites, sessionId, deviceId);
		}
	}

	private async synchronizeTrackingIdsWithServer(currentSessionId: number, currentDeviceId: string): Promise<void> {
		if (currentSessionId === this.options.SessionId && currentDeviceId === this.options.DeviceId) {
			return;
		}

		await axios.get(
			MiaPlaza.Controllers.Tracking.AmplitudeController.GetSetAmplitudeDataUrl(
				currentSessionId,
				currentDeviceId,
			),
		);
	}

	// Sends all queued events to Amplitude and clears the queue.
	private flushEventQueue(): void {
		if (!this.amplitude) {
			return;
		}
		for (const event of amplitudeEventQueue) {
			this.amplitude.track(event.eventType, event.eventProperties);
		}
		amplitudeEventQueue.length = 0;
	}

	/**
	 * Tracks an event. If init has not run yet, the event is queued and sent when load() completes.
	 * load() is only called if we are allowed to track for the user, so calling track() anywhere is privacy compliant.
	 * If init has already ran, the event is sent immediately via the initialized Amplitude SDK.
	 */
	public static track(event: AmplitudeEvent): void {
		if (amplitudeInstance && amplitudeInstance.amplitude) {
			amplitudeInstance.amplitude.track(event.eventType, event.eventProperties);
		}
		else {
			amplitudeEventQueue.push(event);
		}
	}
}
